import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search } from 'lucide-react';
import type { CRMUser, PdfAnnotation } from '../../types';
import RichCommentEditor, {
  RichCommentEditorHandle,
  RichCommentEditorPayload,
} from '../pm/RichCommentEditor';
import { isInternalCommentMentionUser } from '../../utils/mentionAffiliation';
import { groupPlanReviewAnnotationsByPage } from '../../utils/planReviewSummary';
import PlanReviewSummaryPreview from './PlanReviewSummaryPreview';
import '../pm/TaskComments.css';
import './PlanReviewCompleteModal.css';

export interface PlanReviewCompleteModalProps {
  reviewId: string;
  fileName: string;
  reviewAnnotations: PdfAnnotation[];
  defaultRecipientIds: string[];
  crmUsers: CRMUser[];
  onCancel: () => void;
  onConfirm: (payload: {
    notifyUserIds: string[];
    summaryNote: string;
    summaryNoteHtml: string;
  }) => Promise<void>;
}

export default function PlanReviewCompleteModal({
  reviewId,
  fileName,
  reviewAnnotations,
  defaultRecipientIds,
  crmUsers,
  onCancel,
  onConfirm,
}: PlanReviewCompleteModalProps) {
  const noteEditorRef = useRef<RichCommentEditorHandle>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(defaultRecipientIds));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(new Set(defaultRecipientIds));
  }, [defaultRecipientIds]);

  const commentGroups = useMemo(
    () => groupPlanReviewAnnotationsByPage(reviewAnnotations),
    [reviewAnnotations],
  );

  const mentionableUsers = useMemo(
    () => crmUsers.filter((u) => isInternalCommentMentionUser(u) || (u.isActive && u.role === 'cpa')),
    [crmUsers],
  );

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return crmUsers
      .filter((u) => isInternalCommentMentionUser(u))
      .filter((u) => {
        if (!q) return true;
        const name = (u.displayName || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [crmUsers, search]);

  const toggle = (uid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selected.size === 0) {
      setError('Select at least one recipient.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const notePayload: RichCommentEditorPayload = noteEditorRef.current?.getPayload() ?? {
        bodyHtml: '',
        bodyPlain: '',
        mentions: [],
        isEmpty: true,
      };
      await onConfirm({
        notifyUserIds: Array.from(selected),
        summaryNote: notePayload.bodyPlain.trim(),
        summaryNoteHtml: notePayload.isEmpty ? '' : notePayload.bodyHtml,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  return (
    <div className="plan-review-complete-overlay" onClick={onCancel} role="presentation">
      <div
        className="plan-review-complete-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="plan-review-complete-title"
      >
        <header className="plan-review-complete-header">
          <div>
            <h2 id="plan-review-complete-title">Complete plan review</h2>
            <p className="plan-review-complete-sub">
              {fileName}
              {reviewAnnotations.length > 0
                ? ` · ${reviewAnnotations.length} comment${reviewAnnotations.length === 1 ? '' : 's'} in this slice`
                : ' · no comments yet'}
            </p>
          </div>
          <button type="button" className="plan-review-complete-close" onClick={onCancel} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <section className="plan-review-complete-section" aria-labelledby="plan-review-preview-heading">
          <h3 id="plan-review-preview-heading" className="plan-review-complete-label">
            Review preview
          </h3>
          {commentGroups.length === 0 ? (
            <p className="plan-review-complete-preview-empty">
              No PDF comments were added during this review.
            </p>
          ) : (
            <PlanReviewSummaryPreview commentGroups={commentGroups} />
          )}
        </section>

        <label className="plan-review-complete-label" htmlFor="plan-review-note-editor">
          Cover note (optional)
        </label>
        <div id="plan-review-note-editor" className="plan-review-complete-note-editor">
          <RichCommentEditor
            ref={noteEditorRef}
            taskId={`plan-review-${reviewId}`}
            mentionableUsers={mentionableUsers}
            placeholder="Summary for the design team…"
            enablePasteImage
          />
        </div>

        <label className="plan-review-complete-label" htmlFor="plan-review-search">
          Notify
        </label>
        <div className="plan-review-complete-search">
          <Search size={14} aria-hidden />
          <input
            id="plan-review-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search people…"
          />
        </div>
        <ul className="plan-review-complete-list">
          {candidates.map((u) => (
            <li key={u.uid}>
              <label className="plan-review-complete-row">
                <input
                  type="checkbox"
                  checked={selected.has(u.uid)}
                  onChange={() => toggle(u.uid)}
                />
                <span className="plan-review-complete-name">{u.displayName || u.email || u.uid}</span>
                <span className="plan-review-complete-role">{u.role}</span>
              </label>
            </li>
          ))}
          {candidates.length === 0 && (
            <li className="plan-review-complete-empty">No matching users</li>
          )}
        </ul>

        {error && <p className="plan-review-complete-error">{error}</p>}

        <footer className="plan-review-complete-footer">
          <button type="button" className="plan-review-complete-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="plan-review-complete-primary"
            onClick={() => void handleConfirm()}
            disabled={saving || selected.size === 0}
          >
            {saving ? 'Sending…' : `Send summary (${selected.size})`}
          </button>
        </footer>
      </div>
    </div>
  );
}
